import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Phone, ExternalLink, AlertTriangle, Heart } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CrisisPage() {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/crisis-resources`);
      setResources(response.data);
    } catch (error) {
      console.error('Could not fetch crisis resources');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="crisis-page">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="rounded-full hover:bg-secondary/80"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tillbaka
          </Button>
          <h1 className="font-heading text-xl font-light">Krisresurser</h1>
          <div className="w-24" />
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Emergency banner */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8 animate-fade-in">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-1" />
            <div>
              <h2 className="font-heading text-xl text-red-800 mb-2">Vid akut fara</h2>
              <p className="text-red-700 mb-4">
                Om du eller någon annan är i omedelbar fara, ring <strong>112</strong> direkt.
              </p>
              <a 
                href="tel:112"
                className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-full hover:bg-red-700 transition-colors"
              >
                <Phone className="w-5 h-5" />
                Ring 112
              </a>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="text-center mb-10 space-y-3 animate-fade-in">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Heart className="w-7 h-7 text-primary" />
            </div>
          </div>
          <h2 className="font-heading text-3xl font-light">Du är inte ensam</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Det finns människor som vill lyssna och hjälpa. Att söka stöd är ett tecken på styrka.
          </p>
        </div>

        {/* Resources list */}
        <div className="space-y-4">
          {resources.map((resource, index) => (
            <div 
              key={resource.id}
              className="bg-card rounded-2xl p-6 animate-fade-in hover:shadow-soft transition-shadow"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-heading text-xl mb-1">{resource.name}</h3>
                  <p className="text-muted-foreground text-sm mb-2">{resource.description}</p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">Tillgänglig:</span> {resource.available}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {resource.phone && (
                    <a 
                      href={`tel:${resource.phone.replace(/\s/g, '')}`}
                      className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full hover:bg-primary/90 transition-colors text-sm"
                    >
                      <Phone className="w-4 h-4" />
                      {resource.phone}
                    </a>
                  )}
                  {resource.website && (
                    <a 
                      href={resource.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground px-4 py-2 rounded-full hover:bg-secondary/80 transition-colors text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Webbplats
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom message */}
        <div className="mt-12 text-center space-y-4">
          <p className="text-muted-foreground">
            Det spelar ingen roll hur "litet" du tycker att ditt problem är. Om du mår dåligt förtjänar du stöd.
          </p>
          <p className="text-sm text-muted-foreground italic">
            "Att be om hjälp är det modigaste du kan göra."
          </p>
        </div>
      </main>
    </div>
  );
}
